/* ============================================================
   AdminReferralPanel — 2f9c70ba admin referral hub: program
   settings (percentages, TTLs, slug rules, conflict policy)
   plus the commission ledger and attribution list (read-only
   accounting; payouts stay a separate approval).
   ============================================================ */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import type { ReferralLedgerEntry, ReferralProgramSettings, ReferralAttributionRow } from '../types/referral';

export function AdminReferralPanel() {
  const [settings, setSettings] = useState<ReferralProgramSettings | null>(null);
  const [ledger, setLedger] = useState<ReferralLedgerEntry[]>([]);
  const [attributions, setAttributions] = useState<ReferralAttributionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, l, a] = await Promise.all([
        api.getReferralSettings(),
        api.getReferralLedger(20),
        api.getReferralAttributions(20),
      ]);
      setSettings(s);
      setLedger(l);
      setAttributions(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div style={{ padding: 'var(--space-lg)', border: '1px solid var(--color-separator)', borderRadius: 'var(--radius-lg)' }}>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-danger)' }}>{error}</p>
        <button className="btn btn-secondary" onClick={load}>Повторить</button>
      </div>
    );
  }

  if (!settings) {
    return (
      <div style={{ padding: 'var(--space-lg)', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setNote(null);
    try {
      setSettings(await api.saveReferralSettings(settings));
      setNote('Настройки сохранены.');
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const input = { fontSize: 'var(--fs-caption)', padding: '8px 12px' } as const;
  const row = (label: string, node: React.ReactNode) => (
    <label key={label} style={{ display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
      {label}
      {node}
    </label>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      <form
        onSubmit={save}
        style={{ padding: 'var(--space-lg)', border: '1px solid var(--color-separator)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}
      >
        <h3 style={{ fontSize: 'var(--fs-title)', fontWeight: 'var(--fw-bold)', margin: 0 }}>
          Реферальная программа
        </h3>

        <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, fontSize: 'var(--fs-caption)' }}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          />
          Программа включена
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-sm)' }}>
          {row('Доля со своего магазина, % комиссии', (
            <input
              type="number" min="0" max="90" step="0.5" style={input} required
              value={settings.ownShopCommissionPercent}
              onChange={(e) => setSettings({ ...settings, ownShopCommissionPercent: Number(e.target.value) })}
            />
          ))}
          {row('Доля с чужих магазинов, % комиссии', (
            <input
              type="number" min="0" max="90" step="0.5" style={input} required
              value={settings.otherShopCommissionPercent}
              onChange={(e) => setSettings({ ...settings, otherShopCommissionPercent: Number(e.target.value) })}
            />
          ))}
          {row('Срок атрибуции, дней', (
            <input
              type="number" min="1" max="365" style={input} required
              value={settings.attributionTtlDays}
              onChange={(e) => setSettings({ ...settings, attributionTtlDays: Number(e.target.value) })}
            />
          ))}
          {row('Длина адреса: мин–макс', (
            <span style={{ display: 'flex', gap: 4 }}>
              <input
                type="number" min="2" max="64" style={input} required
                value={settings.slugMinLength}
                onChange={(e) => setSettings({ ...settings, slugMinLength: Number(e.target.value) })}
              />
              <input
                type="number" min="3" max="64" style={input} required
                value={settings.slugMaxLength}
                onChange={(e) => setSettings({ ...settings, slugMaxLength: Number(e.target.value) })}
              />
            </span>
          ))}
          {row('Удержание адреса без визитов, дней', (
            <input
              type="number" min="1" max="180" style={input} required
              value={settings.slugHoldDays}
              onChange={(e) => setSettings({ ...settings, slugHoldDays: Number(e.target.value) })}
            />
          ))}
          {row('Пауза между сменами адреса, дней', (
            <input
              type="number" min="0" max="365" style={input} required
              value={settings.slugChangeCooldownDays}
              onChange={(e) => setSettings({ ...settings, slugChangeCooldownDays: Number(e.target.value) })}
            />
          ))}
        </div>

        {row('Зарезервированные адреса (через запятую)', (
          <input
            style={input}
            value={settings.slugReservedNamesCsv}
            onChange={(e) => setSettings({ ...settings, slugReservedNamesCsv: e.target.value })}
          />
        ))}

        <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, fontSize: 'var(--fs-caption)' }}>
          <input
            type="checkbox"
            checked={settings.explicitRefWinsOverSlug}
            onChange={(e) => setSettings({ ...settings, explicitRefWinsOverSlug: e.target.checked })}
          />
          Явная ссылка ?ref= переопределяет атрибуцию по адресу магазина
        </label>

        <button type="submit" className="btn btn-primary" disabled={saving} style={{ alignSelf: 'flex-start' }}>
          Сохранить настройки
        </button>
        {note && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-primary)', margin: 0 }}>{note}</p>}
      </form>

      <div style={{ padding: 'var(--space-lg)', border: '1px solid var(--color-separator)', borderRadius: 'var(--radius-lg)' }}>
        <h3 style={{ fontSize: 'var(--fs-title)', fontWeight: 'var(--fw-bold)', margin: '0 0 var(--space-sm)' }}>
          Атрибуции (последние {attributions.length})
        </h3>
        {attributions.length === 0 ? (
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>Пока нет атрибуций.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {attributions.map((a) => (
              <li key={a.customerUserId} style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                {a.customerName} → {a.referrerName} · {a.source === 'slug' ? 'адрес' : 'ссылка'} ·{' '}
                {a.active ? `до ${new Date(a.expiresUtc).toLocaleDateString('ru-RU')}` : 'истекла'}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ padding: 'var(--space-lg)', border: '1px solid var(--color-separator)', borderRadius: 'var(--radius-lg)' }}>
        <h3 style={{ fontSize: 'var(--fs-title)', fontWeight: 'var(--fw-bold)', margin: '0 0 var(--space-sm)' }}>
          Начисления (последние {ledger.length}) — учёт, без выплат
        </h3>
        {ledger.length === 0 ? (
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>Пока нет начислений.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {ledger.map((l) => (
              <li key={l.orderId} style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                {l.beneficiaryName} · {l.ownShop ? 'свой магазин' : 'чужой магазин'} ·{' '}
                {l.amountRub.toLocaleString('ru-RU')} ₽ ({l.ratePercent}% с комиссии{' '}
                {l.platformFeeRub.toLocaleString('ru-RU')} ₽) · {new Date(l.createdUtc).toLocaleDateString('ru-RU')}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
